import { useState, useCallback } from "react";
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import PlayerHeader from "./Header";
import WaydSection from "./WaydSection";
import WaydSectionSimple from "./WaydSectionSimple";
import TeamReportDataTable from "../TeamReportDataTable";
import UsageAndEfficiencyChart from "../UsageAndEfficiencyChart";
import AdvancedStatsSection from "./AdvancedStatsSection";
import { PlayerPageProps } from "./types";
import { PLAYER_PAGE_USE_ACTIONS_CHART } from "../constants";
import { useLeagueApi } from "../../data/LeagueEnvironmentProvider";
import { useLeagueEnvironment } from "../../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../../data/Cache";
import {
  buildPlayerSectionQuery,
  buildPlayerSectionTableStateParam,
} from "../utils/buildExplorerQuery";
import { FeatureFlags } from "../../util/featureFlags";

const ACTIONS_TITLE = "Halfcourt Offensive Actions";

function SectionExplorerLink({
  loading,
  onClick,
  tooltip = "Open in Explorer",
}: {
  loading: boolean;
  onClick: () => void;
  tooltip?: string;
}) {
  if (!FeatureFlags.adHocExplorerLinks) return null;
  if (loading) {
    return (
      <CircularProgress
        size={16}
        sx={{ color: "text.secondary", ml: 0.5 }}
      />
    );
  }
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          p: "2px",
          color: "text.secondary",
          opacity: 0.5,
          "&:hover": { opacity: 1, color: "primary.main" },
          transition: "opacity 0.15s, color 0.15s",
          "@media print": { display: "none" },
        }}
      >
        <OpenInNewOutlined sx={{ fontSize: "1.265rem" }} />
      </IconButton>
    </Tooltip>
  );
}

export default function PlayerPage({
  playerHeaderProps,
  waydSectionProps,
  advancedStatsProps,
  actionsTableProps,
  closeoutsTableProps,
  showDetailedWaydSection = false,
  teamId,
  teamAbbrev,
  season,
  seasonType,
}: PlayerPageProps) {
  const api = useLeagueApi();
  const environment = useLeagueEnvironment();
  const explorerBasePath =
    environment === ExplorerEnvironment.DRAFT
      ? "/explorer/draft"
      : "/explorer/nba";

  const [explorerLoading, setExplorerLoading] = useState<string | null>(null);

  const openPlayerSectionInExplorer = useCallback(
    async (secondaryDimension: string, dependentFilter: string, loadingKey: string) => {
      setExplorerLoading(loadingKey);
      try {
        const query = buildPlayerSectionQuery({
          season,
          secondaryDimension,
          dependentFilter,
        });
        const { data } = await api.registerQuery(query);
        const tsParam = `&ts=${buildPlayerSectionTableStateParam(teamAbbrev)}`;
        window.open(
          `${explorerBasePath}?q=${data.query_id}${tsParam}`,
          "_blank",
        );
      } catch (err) {
        console.error("Failed to open in Explorer", err);
      } finally {
        setExplorerLoading(null);
      }
    },
    [api, season, explorerBasePath, teamAbbrev],
  );

  return (
    <Stack
      spacing={4}
      sx={{
        ".MuiTypography-h4, .MuiTypography-h5": {
          fontFamily: '"Roboto Condensed", sans-serif',
        },
      }}
    >
      <PlayerHeader {...playerHeaderProps} />
      <Stack sx={{ px: 1 }} spacing={2}>
        <Typography variant="h5">Advanced Stats</Typography>
        <AdvancedStatsSection {...advancedStatsProps} />

        {showDetailedWaydSection && <WaydSection {...waydSectionProps} />}

        {showDetailedWaydSection ? (
          PLAYER_PAGE_USE_ACTIONS_CHART ? (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="h5">{ACTIONS_TITLE}</Typography>
                <SectionExplorerLink
                  loading={explorerLoading === "actions"}
                  onClick={() =>
                    openPlayerSectionInExplorer("ActionSimple", "PrimaryPlayer", "actions")
                  }
                />
              </Stack>
              <Stack alignItems="center">
                <UsageAndEfficiencyChart
                  {...actionsTableProps}
                  width={700}
                  height={450}
                />
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="h5">{ACTIONS_TITLE}</Typography>
                <SectionExplorerLink
                  loading={explorerLoading === "actions"}
                  onClick={() =>
                    openPlayerSectionInExplorer("ActionSimple", "PrimaryPlayer", "actions")
                  }
                />
              </Stack>
              <TeamReportDataTable {...actionsTableProps} teamId={teamId} season={season} seasonType={seasonType} />
            </Stack>
          )
        ) : PLAYER_PAGE_USE_ACTIONS_CHART ? (
          <Stack direction="row" spacing={4}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="h5">{ACTIONS_TITLE}</Typography>
                <SectionExplorerLink
                  loading={explorerLoading === "actions"}
                  onClick={() =>
                    openPlayerSectionInExplorer("ActionSimple", "PrimaryPlayer", "actions")
                  }
                />
              </Stack>
              <UsageAndEfficiencyChart
                {...actionsTableProps}
                width={700}
                height={450}
              />
            </Stack>
            <WaydSectionSimple
              offenseFrequencyData={waydSectionProps.offenseFrequencyData}
              defenseFrequencyData={waydSectionProps.defenseFrequencyData}
              vertical
            />
          </Stack>
        ) : (
          <Stack spacing={4}>
            <WaydSectionSimple
              offenseFrequencyData={waydSectionProps.offenseFrequencyData}
              defenseFrequencyData={waydSectionProps.defenseFrequencyData}
            />
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="h5">{ACTIONS_TITLE}</Typography>
                <SectionExplorerLink
                  loading={explorerLoading === "actions"}
                  onClick={() =>
                    openPlayerSectionInExplorer("ActionSimple", "PrimaryPlayer", "actions")
                  }
                />
              </Stack>
              <TeamReportDataTable {...actionsTableProps} teamId={teamId} season={season} seasonType={seasonType} />
            </Stack>
          </Stack>
        )}

        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h5">Offense vs Closeouts</Typography>
            <SectionExplorerLink
              loading={explorerLoading === "closeouts"}
              onClick={() =>
                openPlayerSectionInExplorer("CloseoutAction", "CloseoutBallhandler", "closeouts")
              }
            />
          </Stack>
          <TeamReportDataTable {...closeoutsTableProps} teamId={teamId} season={season} seasonType={seasonType} />
        </Stack>
      </Stack>
    </Stack>
  );
}
